import { useEffect, useState } from 'react'
import { API_BASE } from './api'

/* ================================================================== */
/*  Public events feed from the myNext50 backend.                      */
/*                                                                     */
/*  These are the *public* (no-auth) endpoints, so unlike api.ts we do  */
/*  NOT send credentials. Dev goes through the same-origin Vite proxy   */
/*  (which strips Origin); in prod the backend must allowlist the       */
/*  LifeCraft origin for the browser call to succeed. Every consumer     */
/*  falls back to the hand-authored static content when the feed is      */
/*  empty or unreachable, so the site never renders a blank section.     */
/* ================================================================== */

const IST = 'Asia/Kolkata'

/* Only these communities are live-wired for now — everything else keeps its
   hand-authored static events. Matched against the backend category name
   (upper-cased). */
const ALLOWED_COMMUNITIES = new Set(['COSMOS', 'RHYTHM', 'ZEN'])

function isAllowed(categoryName?: string): boolean {
  return !!categoryName && ALLOWED_COMMUNITIES.has(categoryName.trim().toUpperCase())
}

/* Backend has placeholder "test" events; never surface them on the site. */
function isTestEvent(e: { title?: string }): boolean {
  return /\btest\b/i.test(e.title || '')
}

/** Raw shape from `GET /events/public` (only the fields we render). */
type RawEvent = {
  _id: string
  title: string
  description?: string
  time?: { start?: string; end?: string }
  venue?: { venue?: string; city?: string; state?: string }
  delivery_mode?: 'online' | 'offline'
  capacity?: number
  price_per_person?: number
  currency?: string
  thumbnail_url?: string
  created_by?: { name?: string; profile_pic_url?: string }
  category?: { _id?: string; name?: string }
}

/** Normalised event for the UI, carrying both the rail and row shapes. */
export type DisplayEvent = {
  id: string
  title: string
  blurb: string
  community: string
  format: 'Online' | 'In person'
  where: string
  when: string // "Sat · 6:30 PM"  (home rail)
  day: string // "Sat"            (community row badge)
  time: string // "6:30pm"         (community row badge)
  kicker: string // "Online · Live on LifeCraft4U"
  image?: string
  hostName?: string
  hostPic?: string
  capacity?: number
  price?: number
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

function timeParts(iso?: string) {
  const empty = { weekday: '', dateNum: '', monthShort: '', dayNum: '', clockLong: '', clockShort: '' }
  if (!iso) return empty
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return empty
  const opt = { timeZone: IST } as const
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short', ...opt })
  const dateNum = d.toLocaleDateString('en-US', { day: 'numeric', ...opt })
  const monthShort = d.toLocaleDateString('en-US', { month: 'short', ...opt })
  const dayNum = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...opt }) // "22 Aug"
  const clockLong = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...opt })
  const clockShort = clockLong.replace(' ', '').replace('AM', 'am').replace('PM', 'pm')
  return { weekday, dateNum, monthShort, dayNum, clockLong, clockShort }
}

function mapEvent(e: RawEvent): DisplayEvent {
  const online = e.delivery_mode === 'online'
  const format: DisplayEvent['format'] = online ? 'Online' : 'In person'
  // Offline events show the city only — never the full street address.
  const where = online ? 'Live on LifeCraft4U' : e.venue?.city?.trim() || 'In person'
  const t = timeParts(e.time?.start)
  const community = e.category?.name ? titleCase(e.category.name) : 'LifeCraft4U'
  return {
    id: e._id,
    title: e.title?.trim() || 'Untitled event',
    blurb: (e.description || '').replace(/[*_#`]/g, '').trim(),
    community,
    format,
    where,
    // Real events are specific dates, so show the calendar date — not just the
    // weekday. Rail pill: "Sat, 16 Aug · 6:30 PM"; row badge: "Sat 16" + time.
    when: [t.weekday && t.dayNum ? `${t.weekday}, ${t.dayNum}` : t.dayNum || t.weekday, t.clockLong]
      .filter(Boolean)
      .join(' · '),
    day: [t.weekday, t.dateNum].filter(Boolean).join(' ') || t.dayNum,
    time: t.clockShort,
    kicker: `${format} · ${online ? 'Live on LifeCraft4U' : where}`,
    image: e.thumbnail_url || undefined,
    hostName: e.created_by?.name,
    hostPic: e.created_by?.profile_pic_url || undefined,
    capacity: e.capacity,
    price: e.price_per_person,
  }
}

async function getPublic<T>(path: string): Promise<T> {
  // No credentials: these endpoints are public and cross-origin credentialed
  // reads would only tighten CORS for no benefit.
  const res = await fetch(`${API_BASE}${path}`)
  if (!res.ok) throw new Error(`events request failed (${res.status})`)
  return res.json() as Promise<T>
}

/** Recent events from the live communities — for the home rail. */
export async function fetchRecentEvents(limit = 6): Promise<DisplayEvent[]> {
  // Over-fetch (the feed isn't filterable by a set of categories server-side),
  // then keep only live-wired communities and genuinely upcoming events, soonest
  // first — so the rail never shows events that have already happened.
  const data = await getPublic<{ results?: RawEvent[] }>(
    `/events/public?upcoming=true&sortBy=time.start:asc&limit=40`,
  )
  const dayStart = new Date()
  dayStart.setHours(0, 0, 0, 0)
  const from = dayStart.getTime()
  return (data.results ?? [])
    .filter((e) => isAllowed(e.category?.name) && !isTestEvent(e))
    .filter((e) => {
      const start = e.time?.start ? new Date(e.time.start).getTime() : NaN
      return !Number.isNaN(start) && start >= from
    })
    .slice(0, limit)
    .map(mapEvent)
}

/* Community name → category id, resolved once from the public categories list
   and memoised for the session. */
let categoryIndex: Promise<Record<string, string>> | null = null
export function communityIds(): Promise<Record<string, string>> {
  if (!categoryIndex) {
    categoryIndex = getPublic<{ results?: { _id?: string; name?: string }[] }>(
      '/communities/public/categories?limit=100',
    )
      .then((d) =>
        Object.fromEntries(
          (d.results ?? [])
            .filter((c) => c._id && c.name)
            // Backend category names now carry taglines, e.g.
            // "COSMOS - Astrology made Simple". We look these up by the short
            // community name ("COSMOS"), so index by BOTH the leading segment
            // (before the dash) and the full name.
            .flatMap((c) => {
              const full = c.name!.trim().toUpperCase()
              const lead = full.split(/\s*[-–—]\s*/)[0].trim()
              return lead && lead !== full
                ? [[lead, c._id!], [full, c._id!]]
                : [[full, c._id!]]
            }),
        ),
      )
      .catch(() => ({}))
  }
  return categoryIndex
}

/** Upcoming events for one community (matched by display name). */
export async function fetchCommunityEvents(communityName: string, limit = 6): Promise<DisplayEvent[]> {
  if (!isAllowed(communityName)) return []
  const id = (await communityIds())[communityName.trim().toUpperCase()]
  if (!id) return []
  const data = await getPublic<{ results?: RawEvent[] }>(
    `/events/public?category=${id}&upcoming=true&sortBy=time.start:asc&limit=40`,
  )
  // Show genuinely upcoming events (from the start of today onward), earliest
  // first. We deliberately do NOT clamp to the current calendar month — that
  // hid real events scheduled a few weeks out and left the section empty.
  const dayStart = new Date()
  dayStart.setHours(0, 0, 0, 0)
  const from = dayStart.getTime()
  return (data.results ?? [])
    .filter((e) => {
      if (isTestEvent(e)) return false
      const start = e.time?.start ? new Date(e.time.start).getTime() : NaN
      return !Number.isNaN(start) && start >= from
    })
    .slice(0, limit)
    .map(mapEvent)
}

/* ------------------------------------------------------------------ */
/*  Hooks: null = still loading (keep showing static), [] = none.      */
/* ------------------------------------------------------------------ */

export function useRecentEvents(limit = 6): DisplayEvent[] | null {
  const [events, setEvents] = useState<DisplayEvent[] | null>(null)
  useEffect(() => {
    let alive = true
    fetchRecentEvents(limit)
      .then((r) => alive && setEvents(r))
      .catch(() => alive && setEvents([]))
    return () => {
      alive = false
    }
  }, [limit])
  return events
}

export function useCommunityEvents(communityName: string | undefined, limit = 6): DisplayEvent[] | null {
  const [events, setEvents] = useState<DisplayEvent[] | null>(null)
  useEffect(() => {
    if (!communityName) {
      setEvents([])
      return
    }
    let alive = true
    setEvents(null)
    fetchCommunityEvents(communityName, limit)
      .then((r) => alive && setEvents(r))
      .catch(() => alive && setEvents([]))
    return () => {
      alive = false
    }
  }, [communityName, limit])
  return events
}
